/**
 * AIIdeasDialog
 *
 * "AI ideas", in two tabs:
 *   Projects     — AI studies open/upcoming funding calls that match the user's profile
 *                  and proposes consortium project ideas with a proposal abstract.
 *   Publications — AI proposes manuscript ideas (article type, target journals,
 *                  research question, abstract) from the user's profile, or from the
 *                  consortium's members and topic when opened from a consortium.
 * Ideas can be edited or refined and turned into a proposal / publication: inside the
 * given consortium, or (from the profile) in a newly created consortium.
 *
 * Props:
 *   open, onClose
 *   initialTab     'projects' | 'publications'
 *   consortium     {object} optional — consortium page context ({ id, name })
 *   onImportCV, onEditProfile  {fn} optional — profile helpers (profile page)
 */
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Alert, LinearProgress, TextField, Chip, Stack, Card, CardContent, CardActions,
  Link, Divider, CircularProgress, Tabs, Tab,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  RocketLaunch as RocketLaunchIcon,
  Event as EventIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as HaveIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import CreditBalance from 'components/common/billing/CreditBalance';
import PaymentDialog from 'components/common/billing/PaymentDialog';
import useBillingBalance from 'components/common/billing/useBillingBalance';
import { useUserContext } from 'shared/contexts/UserContext';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null);
const fitColor = (n) => (n >= 8 ? 'success' : n >= 5 ? 'warning' : 'default');

const errorText = (e, fallback) => {
  const data = e.response?.data;
  return data?.error || (Array.isArray(data) ? data[0] : null)
    || (data && typeof data === 'object' ? Object.values(data).flat()[0] : null) || fallback;
};

const BulletList = ({ title, items }) => (items?.length ? (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="subtitle2">{title}</Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {items.map((x, i) => <li key={i}><Typography variant="body2">{x}</Typography></li>)}
    </Box>
  </Box>
) : null);

function ProjectIdeaCard({ idea, index, busy, onChange, onRefine, onCreate, createLabel }) {
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const call = idea.call || {};
  const set = (key) => (e) => onChange(index, { ...idea, [key]: e.target.value });

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Chip size="small" color={fitColor(idea.fit_score)} label={`Fit ${idea.fit_score}/10`} />
          {call.closes_at && <Chip size="small" icon={<EventIcon />} variant="outlined" label={`Deadline ${fmtDate(call.closes_at)}`} />}
          {idea.duration_months && <Chip size="small" variant="outlined" label={`${idea.duration_months} months`} />}
        </Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {[call.agency, call.program].filter(Boolean).join(' · ')}
        </Typography>
        <Link component={RouterLink} to={`/funding/call/${call.id}/`} target="_blank" variant="body2" fontWeight={600}>
          {call.title}
        </Link>

        <Divider sx={{ my: 1.5 }} />

        {editing ? (
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="Acronym" value={idea.acronym} onChange={set('acronym')} inputProps={{ maxLength: 12 }} sx={{ width: 160 }} />
              <TextField size="small" label="Consortium name" value={idea.consortium_name} onChange={set('consortium_name')} fullWidth />
              <TextField size="small" label="Months" type="number" value={idea.duration_months} onChange={set('duration_months')} sx={{ width: 110 }} />
            </Box>
            <TextField size="small" label="Project title" value={idea.title} onChange={set('title')} fullWidth />
            <TextField label="Proposal abstract" value={idea.abstract} onChange={set('abstract')} fullWidth multiline minRows={6} />
          </Stack>
        ) : (
          <>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              {idea.acronym && <Box component="span" color="primary.main">{idea.acronym}: </Box>}
              {idea.title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{idea.abstract}</Typography>
          </>
        )}

        {idea.fit_rationale && (
          <Alert severity="info" icon={<AutoAwesomeIcon fontSize="small" />} sx={{ mt: 1.5, py: 0 }}>
            <Typography variant="body2">{idea.fit_rationale}</Typography>
          </Alert>
        )}

        {idea.objectives?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Objectives</Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {idea.objectives.map((o, i) => <li key={i}><Typography variant="body2">{o}</Typography></li>)}
            </Box>
          </Box>
        )}

        {idea.partner_roles?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Partners to recruit</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {idea.partner_roles.map((r, i) => (
                <li key={i}>
                  <Typography variant="body2">
                    <b>{r.role}</b>{r.sector ? ` (${r.sector})` : ''}{r.expertise ? ` — ${r.expertise}` : ''}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}

        {idea.suggested_partners?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Researchers on Otable with matching expertise</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
              {idea.suggested_partners.map((p) => (
                <Chip
                  key={p.id}
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to={`/profile/${p.id}/`}
                  target="_blank"
                  clickable
                  label={`${p.name}${p.matched_skills?.length ? ` · ${p.matched_skills.slice(0, 2).join(', ')}` : ''}`}
                />
              ))}
            </Box>
          </Box>
        )}

        {idea.keywords?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
            {idea.keywords.map((k) => <Chip key={k} size="small" label={k} />)}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Tell AI what to change, e.g. “focus more on SMEs, 24 months”"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={!!busy}
          />
          <Button
            variant="outlined"
            startIcon={busy === 'refine' ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!feedback.trim() || !!busy}
            onClick={async () => { if (await onRefine(index, feedback)) setFeedback(''); }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refine
          </Button>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button startIcon={<EditIcon />} onClick={() => setEditing((v) => !v)} disabled={!!busy}>
          {editing ? 'Done editing' : 'Edit'}
        </Button>
        <Button
          variant="contained"
          startIcon={busy === 'create' ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
          onClick={() => onCreate(index)}
          disabled={!!busy || !idea.title?.trim()}
        >
          {createLabel}
        </Button>
      </CardActions>
    </Card>
  );
}

const BASIS = [
  ['research_areas', 'research area', 'research areas'],
  ['skills', 'skill', 'skills'],
  ['publications', 'publication', 'publications'],
  ['projects', 'project', 'projects'],
  ['education', 'education entry', 'education entries'],
  ['work_experience', 'position', 'positions'],
];

function ProfileBasis({ profile, ready, onImportCV, onEditProfile, kind = 'project' }) {
  if (!profile) return <LinearProgress />;
  const counts = profile.counts || {};
  const have = BASIS.filter(([k]) => counts[k] > 0);
  const lacking = (profile.missing || []).filter((m) => m.key !== 'photo').slice(0, 4);
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>Your ideas will be built from your profile:</Typography>
        {have.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {have.map(([k, one, many]) => (
              <Chip key={k} icon={<HaveIcon />} color="success" variant="outlined" size="small"
                    label={`${counts[k]} ${counts[k] === 1 ? one : many}`} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Nothing yet. Your profile is empty.</Typography>
        )}
      </Box>

      {!ready ? (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight={600}>Add research areas or skills to get {kind} ideas.</Typography>
          <Typography variant="body2">
            {kind === 'project'
              ? 'AI project ideas come only from your profile: the calls are matched to your research areas and skills, and the ideas build on your publications and projects.'
              : 'AI publication ideas come only from your profile: they build on your research areas, skills, publications and projects.'}
          </Typography>
        </Alert>
      ) : lacking.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2" fontWeight={600}>Your profile is {profile.score}% complete. A fuller profile gives sharper ideas.</Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {lacking.map((m) => <li key={m.key}><Typography variant="body2">{m.label}: {m.why}</Typography></li>)}
          </Box>
        </Alert>
      )}

      {(lacking.length > 0 || !ready) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onImportCV && (
            <Button variant={ready ? 'outlined' : 'contained'} size="small" startIcon={<UploadIcon />} onClick={onImportCV}>
              Fill from your CV
            </Button>
          )}
          {onEditProfile && (
            <Button size="small" startIcon={<EditIcon />} onClick={onEditProfile}>Complete my profile</Button>
          )}
        </Box>
      )}
    </Stack>
  );
}


function PublicationIdeaCard({ idea, index, busy, onChange, onRefine, onCreate, createLabel }) {
  const [editing, setEditing] = useState(false);
  const [feedback, setFeedback] = useState('');
  const set = (key) => (e) => onChange(index, { ...idea, [key]: e.target.value });
  const journal = idea.target_journal ?? idea.target_journals?.[0] ?? '';

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Chip size="small" icon={<ArticleIcon />} color="primary" variant="outlined" label={idea.article_type_label || idea.article_type} />
        </Box>

        {editing ? (
          <Stack spacing={1.5}>
            <TextField size="small" label="Working title" value={idea.title} onChange={set('title')} fullWidth />
            <TextField size="small" label="Research question" value={idea.research_question} onChange={set('research_question')} fullWidth />
            <TextField label="Abstract" value={idea.abstract} onChange={set('abstract')} fullWidth multiline minRows={6} />
          </Stack>
        ) : (
          <>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>{idea.title}</Typography>
            {idea.research_question && (
              <Typography variant="body2" sx={{ mt: 0.75, fontStyle: 'italic' }}>{idea.research_question}</Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{idea.abstract}</Typography>
          </>
        )}

        {idea.target_journals?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">Target journal</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
              {idea.target_journals.map((j) => (
                <Chip key={j} size="small" label={j} clickable
                  color={j === journal ? 'primary' : 'default'} variant={j === journal ? 'filled' : 'outlined'}
                  onClick={() => onChange(index, { ...idea, target_journal: j })} />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              The chosen journal sets the reference style and section outline.
            </Typography>
          </Box>
        )}

        {idea.fit_rationale && (
          <Alert severity="info" icon={<AutoAwesomeIcon fontSize="small" />} sx={{ mt: 1.5, py: 0 }}>
            <Typography variant="body2">{idea.fit_rationale}</Typography>
          </Alert>
        )}
        <BulletList title="Key contributions" items={idea.key_contributions} />
        <BulletList title="Methods" items={idea.methods} />
        <BulletList title="Data and resources" items={idea.data_and_resources} />
        <BulletList title="Author roles" items={(idea.author_roles || []).map((r) => (r.name ? `${r.name} — ${r.role}` : r.role))} />

        {idea.keywords?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
            {idea.keywords.map((k) => <Chip key={k} size="small" label={k} />)}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <TextField size="small" fullWidth placeholder="Tell AI what to change, e.g. “make it a review article for a clinical audience”"
            value={feedback} onChange={(e) => setFeedback(e.target.value)} disabled={!!busy} />
          <Button variant="outlined" startIcon={busy === 'refine' ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!feedback.trim() || !!busy} sx={{ whiteSpace: 'nowrap' }}
            onClick={async () => { if (await onRefine(index, feedback)) setFeedback(''); }}>
            Refine
          </Button>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button startIcon={<EditIcon />} onClick={() => setEditing((v) => !v)} disabled={!!busy}>
          {editing ? 'Done editing' : 'Edit'}
        </Button>
        <Button variant="contained" onClick={() => onCreate(index)} disabled={!!busy || !idea.title?.trim()}
          startIcon={busy === 'create' ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}>
          {createLabel}
        </Button>
      </CardActions>
    </Card>
  );
}

/**
 * One tab of ideas. kind: 'project' | 'publication'. Generation, refinement and
 * creation endpoints differ; the flow (profile basis -> ideas -> refine -> start) is shared.
 */
function IdeasPanel({ kind, consortium, onClose, onImportCV, onEditProfile, onPaymentNeeded, onSpent, setBusyAny }) {
  const navigate = useNavigate();
  const isProject = kind === 'project';
  const [ideas, setIdeas] = useState(null);
  const [callsConsidered, setCallsConsidered] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  const { data: profile, mutate: refreshProfile } = useSWR('/user-profiles/me/completeness/', fetcher);
  const profileReady = !!profile && ((profile.counts?.research_areas || 0) + (profile.counts?.skills || 0)) > 0;
  // Publication ideas inside a consortium can rely on the members' profiles and its topic
  const ready = profileReady || (!isProject && Boolean(consortium));
  const anyBusy = loading || Object.values(busy).some(Boolean);
  useEffect(() => { setBusyAny(anyBusy); }, [anyBusy, setBusyAny]);

  const context = consortium ? { consortium: consortium.id } : {};

  const fail = (e, fallback) => {
    if (e.response?.status === 402) onPaymentNeeded();
    setError(errorText(e, fallback));
  };

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = isProject ? '/proposal/proposals/ai-ideas/' : '/proposal/proposals/ai-publication-ideas/';
      const res = await axiosService.post(url, context, { timeout: 180000 });
      setIdeas(res.data.ideas || []);
      setCallsConsidered(res.data.calls_considered?.length || 0);
      if (!res.data.ideas?.length) setError('AI could not produce ideas this time. Please try again.');
    } catch (e) {
      if (e.response?.data?.code === 'profile_incomplete') refreshProfile();
      fail(e, 'Failed to generate ideas.');
    } finally {
      setLoading(false);
      onSpent();
    }
  };

  const refine = async (index, feedback) => {
    setBusy((b) => ({ ...b, [index]: 'refine' }));
    setError(null);
    try {
      const url = isProject ? '/proposal/proposals/ai-refine-idea/' : '/proposal/proposals/ai-refine-publication-idea/';
      const res = await axiosService.post(url, { idea: ideas[index], feedback, ...context }, { timeout: 180000 });
      setIdeas((list) => list.map((it, i) => (i === index ? { ...res.data.idea, target_journal: it.target_journal } : it)));
      return true;
    } catch (e) {
      fail(e, 'Failed to refine the idea.');
      return false;
    } finally {
      setBusy((b) => ({ ...b, [index]: null }));
      onSpent();
    }
  };

  const create = async (index) => {
    const idea = ideas[index];
    const message = isProject
      ? (consortium
        ? `Start a proposal for this call in “${consortium.name}”?`
        : `Create the consortium "${idea.consortium_name || idea.acronym}" and start a proposal for this call? You will be its coordinator.`)
      : (consortium
        ? `Start this publication in “${consortium.name}”? All members become authors.`
        : 'Create a new publication consortium for this idea? You will be its coordinator and can invite co-authors.');
    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;
    setBusy((b) => ({ ...b, [index]: 'create' }));
    setError(null);
    try {
      if (isProject) {
        const res = await axiosService.post('/proposal/proposals/create-from-idea/', { idea, ...context });
        const { consortium_id: cid, proposal_id: pid } = res.data;
        onClose();
        navigate(pid ? `/consortium/${cid}/proposals/${pid}/` : `/consortium/${cid}/`);
      } else {
        const res = await axiosService.post('/proposal/proposals/create-publication-from-idea/', { idea, ...context }, { timeout: 60000 });
        onClose();
        navigate(`/consortium/${res.data.consortium_id}/publications/${res.data.publication_id}/`);
      }
    } catch (e) {
      setError(errorText(e, isProject ? 'Failed to create the proposal.' : 'Failed to create the publication.'));
    } finally {
      setBusy((b) => ({ ...b, [index]: null }));
    }
  };

  const IdeaCardComponent = isProject ? ProjectIdeaCard : PublicationIdeaCard;
  const createLabel = isProject
    ? (consortium ? 'Start proposal here' : 'Start consortium & proposal')
    : (consortium ? 'Start publication here' : 'Start publication');

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {!ideas && (
        <>
          <Typography color="text.secondary">
            {isProject
              ? 'One click: AI reviews open and upcoming funding calls, picks the ones that fit your profile, and drafts consortium project ideas with a proposal abstract. Edit or refine an idea, then turn it into a proposal.'
              : consortium
                ? `AI drafts manuscript ideas that build on the expertise of “${consortium.name}” members and its topic, with an article type, suitable journals and an abstract. Edit or refine an idea, then start the publication here.`
                : 'AI drafts manuscript ideas that build on your profile — article type, suitable journals, research question and abstract. Edit or refine an idea, then start the publication.'}
          </Typography>
          {!(consortium && !isProject) && (
            <ProfileBasis profile={profile} ready={profileReady} onImportCV={onImportCV} onEditProfile={onEditProfile} kind={kind} />
          )}
          {loading && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                {isProject ? 'Studying funding calls and drafting ideas…' : 'Drafting publication ideas…'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {ideas && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setIdeas(null)} disabled={anyBusy}>Back</Button>
            <Typography variant="body2" color="text.secondary">
              {isProject ? `Based on ${callsConsidered} matching call${callsConsidered === 1 ? '' : 's'}. ` : ''}
              Refining an idea uses about 1 credit.
            </Typography>
          </Box>
          {ideas.map((idea, i) => (
            <IdeaCardComponent key={i} idea={idea} index={i} busy={busy[i]} createLabel={createLabel}
              onChange={(idx, next) => setIdeas((list) => list.map((it, j) => (j === idx ? next : it)))}
              onRefine={refine} onCreate={create} />
          ))}
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant={ideas ? 'outlined' : 'contained'} onClick={generate} disabled={anyBusy || !ready}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}>
          {ideas ? 'Generate new ideas' : `Generate ${isProject ? 'project' : 'publication'} ideas`}
        </Button>
      </Box>
    </Stack>
  );
}

export default function AIIdeasDialog({ open, onClose, initialTab = 'projects', consortium, onImportCV, onEditProfile }) {
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const { balance, isUnlimited, refresh: refreshBalance } = useBillingBalance();
  const { user } = useUserContext();
  const isAdmin = user?.is_superuser || user?.is_staff || false;

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  const handleClose = () => { if (!busy) onClose?.(); };
  const panelProps = {
    consortium, onClose: handleClose, onImportCV, onEditProfile,
    onPaymentNeeded: () => setPaymentOpen(true), onSpent: refreshBalance, setBusyAny: setBusy,
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
          <AutoAwesomeIcon color="primary" />
          <Box sx={{ flex: 1 }}>AI ideas{consortium ? ` for ${consortium.name}` : ''}</Box>
          <CreditBalance balance={balance} isUnlimited={isAdmin || isUnlimited} onClick={() => setPaymentOpen(true)} />
        </DialogTitle>
        <Tabs value={tab} onChange={(e, v) => !busy && setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="projects" label="Projects" />
          <Tab value="publications" label="Publications" />
        </Tabs>
        <DialogContent dividers>
          {/* Both panels stay mounted so switching tabs keeps generated ideas */}
          <Box sx={{ display: tab === 'projects' ? 'block' : 'none' }}>
            <IdeasPanel kind="project" {...panelProps} />
          </Box>
          <Box sx={{ display: tab === 'publications' ? 'block' : 'none' }}>
            <IdeasPanel kind="publication" {...panelProps} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>

      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        productName="AI Credits"
        productDescription="Credits cover AI token costs. Generating ideas uses about 2 credits; refining one uses about 1."
        unitLabel="credits"
        costPerOperation={2}
        operationLabel="idea generation"
        currentBalance={balance ?? 0}
        onSuccess={() => { refreshBalance(); setPaymentOpen(false); }}
      />
    </>
  );
}
